// Mesure over a timed window instead of every bytes.
const MEASUREMENT_WINDOW_MS = 1000;
const SMOOTHING_HALF_LIFE_MS = 10_000;

/**
 * Seconds left of one file's upload, from its recent speed.
 *
 * note: the estimate only moves when the caller reports progress, so a connection that stalls
 * dead keeps the last one.
 */
export const createRemainingTimeEstimator = (): ((syncedBytes: number, totalBytes: number) => number | undefined) => {
  let windowStart: { atMs: number; syncedBytes: number } | undefined;
  let smoothedBytesPerSecond: number | undefined;

  return (syncedBytes: number, totalBytes: number): number | undefined => {
    const nowMs = Date.now();
    if (!windowStart) {
      windowStart = { atMs: nowMs, syncedBytes };
      return undefined;
    }

    const elapsedMs = nowMs - windowStart.atMs;
    // Negative bytes mean the count went backwards (a restarted part) — nothing to read from it.
    const windowBytes = syncedBytes - windowStart.syncedBytes;
    if (elapsedMs >= MEASUREMENT_WINDOW_MS && windowBytes >= 0) {
      const measuredBytesPerSecond = (windowBytes / elapsedMs) * 1000;
      // Weighted by how long the window covers, not by being one more sample: the tick rate follows
      // the connection, so a per-sample weight over-reacts on a fast one and lags on a slow one.
      const newMeasurementWeight = 1 - Math.exp(-elapsedMs / SMOOTHING_HALF_LIFE_MS);
      smoothedBytesPerSecond =
        smoothedBytesPerSecond === undefined
          ? measuredBytesPerSecond
          : newMeasurementWeight * measuredBytesPerSecond + (1 - newMeasurementWeight) * smoothedBytesPerSecond;
      windowStart = { atMs: nowMs, syncedBytes };
    }

    if (!smoothedBytesPerSecond || smoothedBytesPerSecond <= 0) {
      return undefined;
    }
    return Math.ceil((totalBytes - syncedBytes) / smoothedBytesPerSecond);
  };
};
